import React, { useState, useEffect, useRef } from 'react';
import { Subtitle, ProcessStatus, TokenUsage, HistoryItem } from './types';
import Dropzone from './components/Dropzone';
import SubtitleEditor from './components/SubtitleEditor';
import Navbar from './components/Navbar';
import PricingModal from './components/PricingModal';
import ProgressBar from './components/ProgressBar';
import AdminDashboard from './components/AdminDashboard'; 
import HistoryList from './components/HistoryList';
import UserProfile from './components/UserProfile';
import { useAuth } from './contexts/AuthContext';
import { generateSubtitlesFromVideo } from './services/geminiService';
import { uploadFileToFirebase, deductCredit, saveHistoryItem, getUserHistory, deleteHistoryItem, getFileDownloadUrl } from './services/firebase';
import { fileToBase64, generateSrtContent, downloadSrt, extractAudioAsBase64, base64ToBlob, getVideoDuration, detectVoiceOnset } from './utils';
import { PLAN_LIMITS, SUPPORTED_LANGUAGES, PLANS } from './constants';
import { Download, AlertCircle, FileText, Globe, Zap, PlayCircle, ShieldCheck, ArrowRight, CheckCircle, Crown, Shield, Clock, LayoutGrid, AlertTriangle, AlignLeft, Sparkles, Target } from 'lucide-react';
import AuthModal from './components/AuthModal';

const App: React.FC = () => {
  const { user, loading: authLoading, credits, refreshCredits, role, tier } = useAuth();
  
  const [file, setFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.IDLE);
  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const [detectedLanguage, setDetectedLanguage] = useState<string>('Unknown');
  const [usage, setUsage] = useState<TokenUsage | undefined>(undefined);
  const [targetLang, setTargetLang] = useState<string>('English');
  const [maxWords, setMaxWords] = useState<number>(10); 
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Dual Progress State
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);

  const [fastMode, setFastMode] = useState<boolean>(true); 
  const [precisionMode, setPrecisionMode] = useState<boolean>(true); // Default to True now for better timestamps
  
  // Navigation State
  const [view, setView] = useState<'create' | 'history' | 'profile'>('create');
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const simulationRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (videoUrl && !videoUrl.startsWith('http')) {
         URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  // Handle Smart Purchase Flow (Intent persistence)
  useEffect(() => {
    if (user) {
      loadHistory();
      
      const pendingPlan = localStorage.getItem('pendingPlanIntent');
      if (pendingPlan) {
        localStorage.removeItem('pendingPlanIntent');
        setShowPricing(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (file && user) {
        checkValidation();
    } else {
        setValidationError(null);
    }
  }, [file, tier, targetLang, videoDuration]);


  useEffect(() => {
    const limits = PLAN_LIMITS[tier];
    if (limits && limits.allowedLanguages !== 'all') {
      if (!limits.allowedLanguages.includes(targetLang)) {
        setTargetLang('English');
      }
    }
  }, [tier, targetLang]);

  const loadHistory = async () => {
    if (!user) return;
    setLoadingHistory(true);
    const items = await getUserHistory(user.uid);
    setHistoryItems(items);
    setLoadingHistory(false);
  };

  const handleSmartPurchase = (planId: string) => {
    if (user) {
      setShowPricing(true);
    } else {
      localStorage.setItem('pendingPlanIntent', planId);
      setShowAuthModal(true);
    }
  };

  const handleDeleteHistory = async (id: string, path: string) => {
    if (!user || !window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await deleteHistoryItem(user.uid, id, path);
      setHistoryItems(prev => prev.filter(item => item.id !== id));
    } catch (e) {
      alert("Failed to delete item");
    }
  };

  const handleLoadFromHistory = async (item: HistoryItem) => {
    setSubtitles(item.subtitles);
    setDetectedLanguage(item.detectedLanguage);
    setTargetLang(item.targetLanguage);
    setFile(null); 
    setVideoUrl(null); 
    setView('create');
    setStatus(ProcessStatus.COMPLETED);
    setExtractionProgress(100);
    setGenerationProgress(100);
    
    if (item.subtitles.length > 0) {
       const lastSub = item.subtitles[item.subtitles.length - 1];
       const parts = lastSub.endTime.replace(',','.').split(':');
       if (parts.length === 3) {
          setVideoDuration((parseFloat(parts[0]) * 3600) + (parseFloat(parts[1]) * 60) + parseFloat(parts[2]));
       }
    }

    if (item.storagePath) {
       const url = await getFileDownloadUrl(item.storagePath);
       if (url) {
         setVideoUrl(url);
       } else {
         console.warn("Video file not found or expired.");
       }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileSelect = async (selectedFile: File) => {
    try {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setVideoUrl(url);

      // Get Duration for validation
      const duration = await getVideoDuration(selectedFile);
      setVideoDuration(duration);

      setSubtitles([]);
      setUsage(undefined);
      setStatus(ProcessStatus.IDLE);
      setExtractionProgress(0);
      setGenerationProgress(0);
      setErrorMsg(null);
      setUploadUrl(null);
      setFirebaseError(null);
    } catch (e) {
      setErrorMsg("Error loading video metadata.");
    }
  };

  const updateSubtitle = (id: number, field: 'text' | 'translatedText', value: string) => {
    setSubtitles(prev => prev.map(sub => 
      sub.id === id ? { ...sub, [field]: value } : sub
    ));
  };
  
  const handleBulkUpdateSubtitles = (newSubs: Subtitle[]) => {
      setSubtitles(newSubs);
  };

  const checkValidation = (): boolean => {
    if (!user) return false;
    const limits = PLAN_LIMITS[tier];

    if (videoDuration > limits.maxDurationSeconds) {
      const maxMins = limits.maxDurationSeconds / 60;
      setValidationError(`Your ${tier.toUpperCase()} plan is limited to ${maxMins} mins. This video is ${(videoDuration/60).toFixed(1)} mins.`);
      return false;
    }

    if (limits.allowedLanguages !== 'all') {
      if (!limits.allowedLanguages.includes(targetLang)) {
        setValidationError(`The ${tier.toUpperCase()} plan does not support ${targetLang}.`);
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const handleProcess = async () => {
    if (!file || !user) return;

    if (!checkValidation()) {
        setShowPricing(true);
        return;
    }

    if (credits <= 0) {
      setShowPricing(true);
      return;
    }

    setStatus(ProcessStatus.EXTRACTING);
    setExtractionProgress(10);
    setGenerationProgress(0);
    setErrorMsg(null);
    setUploadUrl(null);
    setFirebaseError(null);
    
    let storagePath = ''; 

    try {
      await deductCredit(user.uid);
      await refreshCredits();

      console.log("Analyzing audio waveform...");
      const detectedOnset = await detectVoiceOnset(file);
      console.log("Detected Voice Onset:", detectedOnset, "seconds");

      let base64Data = "";
      let mimeType = file.type;
      let blobToUpload: Blob = file;
      let fileName = file.name;

      if (fastMode) {
        console.log("Starting audio extraction...");
        setExtractionProgress(30);
        base64Data = await extractAudioAsBase64(file);
        mimeType = "audio/wav"; 
        blobToUpload = base64ToBlob(base64Data, mimeType);
        fileName = `${file.name.split('.')[0]}.wav`;
        setExtractionProgress(50);
      } else {
        base64Data = await fileToBase64(file);
        blobToUpload = file;
        setExtractionProgress(30);
      }

      try {
        setStatus(ProcessStatus.UPLOADING);
        const folder = fastMode ? 'optimized_audio' : 'raw_videos';
        const uniqueName = `${Date.now()}_${fileName}`;
        storagePath = `users/${user.uid}/${folder}/${uniqueName}`;
        
        const url = await uploadFileToFirebase(blobToUpload, storagePath, (p) => {
          // Map firebase progress (0-100) to our extractionProgress range (50-100)
          const mappedProgress = 50 + (p * 0.5); 
          setExtractionProgress(mappedProgress);
        });
        setUploadUrl(url);
        setExtractionProgress(100); 
      } catch (err: any) {
        console.warn("Firebase upload failed:", err);
        setFirebaseError(`Upload skipped: ${err.message}`);
        storagePath = ''; 
        setExtractionProgress(100); 
      }
      
      setStatus(ProcessStatus.ANALYZING);
      startGenerationSimulation(); // Start the 2nd bar

      const result = await generateSubtitlesFromVideo(
          base64Data, 
          mimeType, 
          targetLang, 
          maxWords,
          detectedOnset, 
          precisionMode,
          videoDuration 
      );
      
      stopGenerationSimulation();
      setGenerationProgress(100);

      setSubtitles(result.subtitles);
      setDetectedLanguage(result.detectedLanguage);
      setUsage(result.usage);
      setStatus(ProcessStatus.COMPLETED);

      await saveHistoryItem(user.uid, tier, {
        fileName: file.name,
        originalName: file.name,
        storagePath: storagePath, 
        subtitles: result.subtitles,
        detectedLanguage: result.detectedLanguage,
        targetLanguage: targetLang,
        usage: result.usage,
        modelUsed: precisionMode ? 'pro' : 'flash'
      });
      loadHistory(); 

    } catch (err: any) {
      console.error(err);
      stopGenerationSimulation();
      setErrorMsg(err.message || "An unexpected error occurred.");
      setStatus(ProcessStatus.ERROR);
      setExtractionProgress(0);
      setGenerationProgress(0);
    }
  };

  const startGenerationSimulation = () => {
    if (simulationRef.current) clearInterval(simulationRef.current);
    // Simulate generation time based on video duration roughly
    // Assuming 1s of video takes 0.2s to process (fast) or 0.5s (pro)
    const estimatedTime = precisionMode ? videoDuration * 0.5 : videoDuration * 0.2;
    const intervalTime = (estimatedTime * 1000) / 100;

    simulationRef.current = setInterval(() => {
      setGenerationProgress((prev) => {
        if (prev >= 95) return prev;
        return prev + 1;
      });
    }, intervalTime > 50 ? intervalTime : 50); 
  };

  const stopGenerationSimulation = () => {
    if (simulationRef.current) clearInterval(simulationRef.current);
  };

  const handleExport = (useTranslation: boolean) => {
    const content = generateSrtContent(subtitles, useTranslation);
    const suffix = useTranslation ? `_${targetLang}` : '_original';
    const filename = file ? `${file.name.split('.')[0]}${suffix}.srt` : `subtitles${suffix}.srt`;
    downloadSrt(content, filename);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (showAdmin && role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-950">
        <div className="fixed top-4 right-4 z-50">
           <button 
              onClick={() => setShowAdmin(false)}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-700 transition-colors flex items-center gap-2"
           >
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to App
           </button>
        </div>
        <AdminDashboard />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 text-white font-sans selection:bg-blue-500/30">
        <Navbar onOpenProfile={() => {}} onOpenHome={() => {}} />
        
        <div className="relative pt-32 pb-20 px-6 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
              Subtitles generated by AI <br /> in seconds.
            </h1>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Upload your video, and let our advanced AI generate perfectly synchronized subtitles in over 40 languages. 
            </p>
            <div className="flex justify-center">
              <button onClick={() => setShowAuthModal(true)} className="group px-8 py-4 bg-white text-gray-950 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:scale-105">
                Start for Free <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        <div className="py-20 bg-gray-900/50 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
              <p className="text-gray-400">Start with free credits, upgrade when you need more.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {PLANS.map((plan) => {
                const isRecommended = plan.recommended;
                const Icon = plan.id === 'pro' ? Crown : plan.id === 'creator' ? Zap : Shield;

                return (
                  <div 
                    key={plan.id}
                    className={`
                      relative flex flex-col p-8 rounded-3xl border transition-all duration-300
                      ${isRecommended 
                        ? 'bg-gradient-to-b from-blue-900/40 to-blue-950/40 border-blue-500/50 shadow-2xl shadow-blue-900/20 scale-105 z-10' 
                        : 'bg-gray-800/40 border-gray-700/50 hover:border-gray-600 hover:bg-gray-800/60'}
                    `}
                  >
                     {isRecommended && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold tracking-wider shadow-lg">
                        MOST POPULAR
                      </div>
                    )}

                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-3 rounded-xl ${isRecommended ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">{plan.tier} Tier</p>
                      </div>
                    </div>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-4xl font-bold text-white">${plan.price}</span>
                      <span className="text-gray-400 text-sm">/ {plan.credits} videos</span>
                    </div>

                    <div className="space-y-4 flex-1 mb-8">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                          <CheckCircle className={`w-4 h-4 shrink-0 ${isRecommended ? 'text-blue-400' : 'text-gray-500'}`} />
                          {feature}
                        </div>
                      ))}
                    </div>

                    <button 
                      onClick={() => handleSmartPurchase(plan.id)}
                      className={`
                        w-full py-3 rounded-xl font-bold transition-all
                        ${isRecommended 
                          ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20' 
                          : 'bg-white/10 hover:bg-white/20 text-white'}
                      `}
                    >
                      Choose Plan
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-[#0f172a] to-[#1e1b4b] text-white pt-24 pb-12 px-6 md:px-12 font-sans selection:bg-blue-500/30">
      <Navbar onOpenProfile={() => setView('profile')} onOpenHome={() => setView('create')} />
      <PricingModal isOpen={showPricing} onClose={() => setShowPricing(false)} />

      <div className="max-w-7xl mx-auto">
        <header className="mb-8 relative z-10 flex flex-col md:flex-row justify-between items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {view === 'create' ? 'Studio Dashboard' : view === 'history' ? 'Project History' : 'My Profile'}
            </h1>
            <div className="flex items-center gap-2">
               <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-widest bg-gray-800 border border-gray-600 text-gray-400">
                 Current Plan: <span className="text-blue-400">{tier}</span>
               </span>
               {tier === 'free' && (
                 <button onClick={() => setShowPricing(true)} className="text-xs text-blue-400 hover:underline">
                    Upgrade
                 </button>
               )}
            </div>
          </div>
          
          <div className="flex items-center gap-3 bg-gray-900/50 p-1 rounded-xl border border-white/10">
            <button 
              onClick={() => setView('create')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${view === 'create' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <LayoutGrid className="w-4 h-4" /> Studio
            </button>
            <button 
              onClick={() => setView('history')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${view === 'history' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <Clock className="w-4 h-4" /> History
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowPricing(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-600/20 to-yellow-500/20 border border-yellow-500/30 text-yellow-200 rounded-lg text-sm font-semibold hover:bg-yellow-500/30 transition-all"
            >
              <Zap className="w-4 h-4" /> Get Credits
            </button>
            
            {role === 'admin' && (
              <button 
                onClick={() => setShowAdmin(true)}
                className="p-2 rounded-lg border bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors flex items-center gap-2 px-4"
              >
                <ShieldCheck className="w-5 h-5" /> Admin
              </button>
            )}
          </div>
        </header>

        {view === 'profile' && (
          <UserProfile onOpenPricing={() => setShowPricing(true)} />
        )}

        {view === 'history' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {loadingHistory ? (
              <div className="flex justify-center p-12">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <HistoryList 
                items={historyItems} 
                onDelete={handleDeleteHistory}
                onLoad={handleLoadFromHistory}
              />
            )}
          </div>
        )}
        
        {view === 'create' && (
          <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              
              {!videoUrl && (
                  <div className="w-full">
                     <div className="max-w-3xl mx-auto">
                        <div className="bg-gray-900/40 backdrop-blur-md rounded-3xl border border-white/10 p-8 shadow-2xl">
                            <h2 className="text-xl font-bold mb-6 flex items-center text-white">
                              <FileText className="w-5 h-5 mr-3 text-blue-400" />
                              Upload Video to Start
                            </h2>
                            <Dropzone onFileSelected={handleFileSelect} />
                        </div>
                     </div>
                  </div>
              )}

              {videoUrl && (
                  <>
                     <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-900/40 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl lg:col-span-1">
                            <div className="flex items-center space-x-4 overflow-hidden mb-4">
                              <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-3 rounded-xl border border-white/5">
                                <PlayCircle className="w-6 h-6 text-blue-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-200 truncate">{file?.name || 'Remote Video'}</p>
                                <div className="flex gap-3 text-xs text-gray-400 font-mono mt-1">
                                  {file && <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>}
                                  <span className={videoDuration > PLAN_LIMITS[tier].maxDurationSeconds ? "text-red-400 font-bold" : "text-green-400"}>
                                    {(videoDuration / 60).toFixed(1)} mins
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {(status !== ProcessStatus.IDLE && status !== ProcessStatus.COMPLETED) && (
                               <ProgressBar 
                                  extractionProgress={extractionProgress}
                                  generationProgress={generationProgress}
                                  status={status} 
                               />
                            )}

                            {errorMsg && (
                              <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-xl flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-red-200">{errorMsg}</p>
                              </div>
                            )}
                            
                            {validationError && (
                              <div className="mt-4 bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex flex-col items-center text-center">
                                  <p className="text-sm text-red-200 mb-2">{validationError}</p>
                                  <button onClick={() => setShowPricing(true)} className="text-xs font-bold text-red-400 hover:text-white underline">Upgrade Now</button>
                              </div>
                            )}
                        </div>

                        <div className="bg-gray-900/40 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl lg:col-span-2 flex flex-col justify-between">
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => setFastMode(!fastMode)}
                                        disabled={status !== ProcessStatus.IDLE && status !== ProcessStatus.COMPLETED}
                                        className={`flex-1 p-3 rounded-xl border text-left transition-all ${fastMode ? 'bg-blue-600/10 border-blue-500/40' : 'bg-gray-800/30 border-white/5 hover:bg-gray-800/50'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                        <Zap className={`w-4 h-4 ${fastMode ? 'text-blue-400' : 'text-gray-500'}`} />
                                        <span className={`text-sm font-bold ${fastMode ? 'text-blue-200' : 'text-gray-400'}`}>Fast Upload</span>
                                        </div>
                                    </button>
                                    
                                    <button 
                                        onClick={() => setPrecisionMode(!precisionMode)}
                                        disabled={status !== ProcessStatus.IDLE && status !== ProcessStatus.COMPLETED}
                                        className={`flex-1 p-3 rounded-xl border text-left transition-all ${precisionMode ? 'bg-purple-600/10 border-purple-500/40' : 'bg-gray-800/30 border-white/5 hover:bg-gray-800/50'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                        <Target className={`w-4 h-4 ${precisionMode ? 'text-purple-400' : 'text-gray-500'}`} />
                                        <span className={`text-sm font-bold ${precisionMode ? 'text-purple-200' : 'text-gray-400'}`}>High Precision</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500">{precisionMode ? 'Gemini 3.0 Pro' : 'Gemini 3.0 Flash'}</p>
                                    </button>
                                </div>

                                <div className="flex gap-2">
                                  <div className="flex-1 relative group">
                                      <div className="absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none">
                                          <AlignLeft className="w-4 h-4 text-gray-500" />
                                      </div>
                                      <select 
                                          value={maxWords}
                                          onChange={(e) => setMaxWords(Number(e.target.value))}
                                          disabled={status !== ProcessStatus.IDLE && status !== ProcessStatus.COMPLETED}
                                          className="w-full h-full bg-gray-800/30 border border-white/5 text-gray-300 text-sm rounded-xl pl-10 p-2 appearance-none hover:bg-gray-800/50 transition-colors cursor-pointer"
                                      >
                                          <option value={5}>Short (5 words)</option>
                                          <option value={10}>Standard (10 words)</option>
                                          <option value={15}>Long (15 words)</option>
                                      </select>
                                  </div>

                                  <div className="flex-1 relative group">
                                    <div className="absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none">
                                      <Globe className="w-4 h-4 text-gray-500" />
                                    </div>
                                    <select 
                                        value={targetLang}
                                        onChange={(e) => setTargetLang(e.target.value)}
                                        className="w-full h-full bg-gray-800/30 border border-white/5 text-gray-300 text-sm rounded-xl pl-10 p-4 appearance-none hover:bg-gray-800/50 transition-colors cursor-pointer"
                                        disabled={status !== ProcessStatus.IDLE && status !== ProcessStatus.COMPLETED}
                                    >
                                      {PLAN_LIMITS[tier].allowedLanguages === 'all' 
                                        ? SUPPORTED_LANGUAGES.map(lang => <option key={lang} value={lang}>{lang}</option>)
                                        : (PLAN_LIMITS[tier].allowedLanguages as string[]).map(lang => <option key={lang} value={lang}>{lang}</option>)
                                      }
                                    </select>
                                  </div>
                                </div>
                             </div>

                             <div className="flex gap-4">
                                {status === ProcessStatus.COMPLETED ? (
                                    <>
                                      <button 
                                        onClick={() => handleExport(false)}
                                        className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl bg-gray-800 border border-white/10 hover:bg-gray-700 transition-all text-white font-bold"
                                      >
                                        <Download className="w-4 h-4" /> SRT (Original)
                                      </button>
                                      <button 
                                        onClick={() => handleExport(true)}
                                        className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl bg-blue-600 border border-blue-500 hover:bg-blue-500 transition-all text-white font-bold"
                                      >
                                        <Download className="w-4 h-4" /> SRT ({targetLang})
                                      </button>
                                      <button onClick={() => { setFile(null); setVideoUrl(null); }} className="px-4 rounded-xl border border-red-500/20 text-red-400 hover:bg-red-500/10">Reset</button>
                                    </>
                                ) : (
                                    <button
                                      onClick={handleProcess}
                                      disabled={status === ProcessStatus.ANALYZING || status === ProcessStatus.UPLOADING || status === ProcessStatus.EXTRACTING}
                                      className={`
                                        flex-1 py-4 px-6 rounded-xl font-bold text-sm uppercase tracking-wide transition-all shadow-lg transform active:scale-[0.99]
                                        ${validationError 
                                              ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                                              : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-blue-900/20'
                                        }
                                      `}
                                    >
                                      {status === ProcessStatus.IDLE ? 'Generate Subtitles (1 Credit)' : 'Processing...'}
                                    </button>
                                )}
                             </div>
                        </div>
                     </div>
                     
                     <div className="w-full">
                        <SubtitleEditor 
                            subtitles={subtitles} 
                            detectedLanguage={detectedLanguage} 
                            usage={usage} 
                            videoUrl={videoUrl}
                            videoDuration={videoDuration}
                            onUpdateSubtitle={updateSubtitle}
                            onBulkUpdate={handleBulkUpdateSubtitles}
                        />
                     </div>
                  </>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
